// Renders one order: summary plus its collapsible shipment, address and item sections.

import { useState } from 'react';
import { House, Package, Truck } from 'lucide-react';
import { TruncatedText } from '@/components/shared/truncated-text';
import { CollapseBody, CollapseHeader } from './collapse-toggle';
import { statusStyle } from './status-style';
import { cn } from '@/lib/utils';
import { FieldRow } from './field-row';
import { SectionCard } from './section-card';
import { StatTile } from './stat-tile';
import type { WorkspaceOrderDto } from './types';

/** The provider fills these blocks with empty strings, so presence alone is not data. */
function hasText(block: Record<string, string> | null): boolean {
  return block !== null && Object.values(block).some((value) => value.trim());
}

export function OrderCard({ order }: { order: WorkspaceOrderDto }) {
  const [open, setOpen] = useState(true);
  // A section with nothing in it is noise, so each one renders only when it has data.
  const shipment = hasText(order.shipment) ? order.shipment : null;
  const address = hasText(order.shippingAddress) ? order.shippingAddress : null;
  const hasDetails = order.items.length > 0 || Boolean(order.shippingCost);

  return (
    <div className="flex w-full flex-col rounded-xl border border-gray-100 bg-white px-2 pb-[18px] pt-2 drop-shadow-[0_0_3px_rgba(0,0,0,0.06)]">
      <div className="flex w-full flex-col gap-2">
        <CollapseHeader open={open} label={`order ${order.externalId}`} onToggle={() => setOpen((prev) => !prev)} className="items-start">
          <div className="flex flex-col gap-2">
            <p className="text-[15px] font-medium leading-[1.1] text-text-body">{order.externalId}</p>
            <div className="flex items-center gap-1">
              {order.statuses.map((status) => (
                <span
                  key={status}
                  className={cn('rounded-xl px-3 py-1 text-[12px] font-medium leading-[1.1]', statusStyle(status))}
                >
                  {status}
                </span>
              ))}
            </div>
          </div>
        </CollapseHeader>
        <div className="flex w-full items-center justify-between gap-3">
          <div className="flex flex-col gap-1.5 text-[12px] leading-[1.1]">
            <p className="text-gray-700">Ordered on</p>
            <p className="font-semibold text-text-prose">{order.orderedOn}</p>
          </div>
          {order.totalSpent && <StatTile value={order.totalSpent} label="Total spent" bordered />}
        </div>
      </div>

      <CollapseBody open={open}>
        <div className="flex w-full flex-col gap-4 pt-4">
          {shipment && (
            <SectionCard icon={<Truck className="size-3.5" />} title="Shipment">
              <div className="flex items-start gap-3">
                {shipment.provider && (
                  <div className="flex flex-col gap-1">
                    <p className="text-[12px] font-medium leading-[1.1] text-gray-700">Provider</p>
                    <p className="text-[12px] leading-[1.1] text-text-body">{shipment.provider}</p>
                  </div>
                )}
                {shipment.trackingId && (
                  <div className="flex flex-col gap-1">
                    <p className="text-[12px] font-medium leading-[1.1] text-gray-700">Tracking ID</p>
                    <TruncatedText text={shipment.trackingId} className="text-[12px] leading-[1.1] text-text-body" />
                  </div>
                )}
                {shipment.link && (
                  <div className="flex min-w-0 flex-col gap-1">
                    <p className="text-[12px] font-medium leading-[1.1] text-gray-700">Link</p>
                    <TruncatedText
                      text={shipment.link}
                      href={shipment.link}
                      className="text-[12px] leading-[1.1] text-blue-500"
                    />
                  </div>
                )}
              </div>
            </SectionCard>
          )}

          {shipment && address && <div className="h-px w-full bg-gray-200" />}

          {address && (
            <SectionCard icon={<House className="size-3.5" />} title="Shipping Address">
              <div className="flex flex-col gap-1.5">
                {address.name && <FieldRow label="Name" value={address.name} />}
                {address.address1 && <FieldRow label="Address 1" value={address.address1} />}
                {address.address2 && <FieldRow label="Address 2" value={address.address2} />}
                {address.city && <FieldRow label="City" value={address.city} />}
                {address.country && <FieldRow label="Country" value={address.country} />}
                {address.zipcode && <FieldRow label="Zipcode" value={address.zipcode} />}
              </div>
            </SectionCard>
          )}

          {(shipment || address) && hasDetails && <div className="h-px w-full bg-gray-200" />}

          {hasDetails && (
            <SectionCard icon={<Package className="size-3.5" />} title="Order Details">
              <div className="flex flex-col gap-2.5">
                <div className="flex w-full items-center justify-between text-[12px] font-medium leading-[1.1] text-gray-600">
                  <p>Products</p>
                  <p className="w-[52px] text-right">Price</p>
                </div>
                {order.items.map((item, index) => (
                  <div key={index} className="flex flex-col gap-2.5">
                    {index > 0 && <div className="h-px w-full bg-gray-200" />}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 flex-1 items-start gap-1 text-[12px] leading-[1.1]">
                        <span className="shrink-0 font-medium text-gray-700">{item.quantity}x</span>
                        <TruncatedText text={item.name} className="min-w-0 text-[12px] leading-[1.1] text-text-body" />
                      </div>
                      <p className="shrink-0 text-right text-[12px] font-medium leading-[1.1] text-text-prose">
                        {item.price}
                      </p>
                    </div>
                  </div>
                ))}
                {order.shippingCost && (
                  <>
                    <div className="h-px w-full bg-gray-200" />
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-1 text-[12px] leading-[1.1]">
                        <p className="text-text-body">Shipping Cost</p>
                        <p className="italic text-gray-700">{order.shippingCost.label}</p>
                      </div>
                      <p className="w-[50px] text-right text-[12px] font-medium leading-[1.1] text-text-prose">
                        {order.shippingCost.amount}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </SectionCard>
          )}
        </div>
      </CollapseBody>
    </div>
  );
}
